import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import EmbedCodeModal from '../components/forms/EmbedCodeModal';
import { publicFormUrl } from '../lib/formUtils';
import {
    Plus, Search, ClipboardEdit, Loader2, Trash2, Edit3, Copy, Code,
    Eye, ToggleLeft, ToggleRight, Users
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * The event a form registers respondents on, for the card's meta row (#919, E55). It reads the form document's
 * `event_registration_event`, so the list makes no extra request. A target the server could not resolve (a deleted
 * event) reads "Registers on: event unavailable" in amber; a form with no target has no chip.
 */
function EventChip({ form }) {
    const target = form?.event_registration_event;
    let text = null;
    let unavailable = false;
    if (target === undefined) {
        // A document with no such key (the list always sends it) says nothing rather than guessing "unavailable".
    } else if (target && typeof target === 'object' && target.name && target.status !== 'deleted') {
        text = `Event: ${target.name}`;
    } else if (target || form?.settings?.event_registration?.event_id) {
        text = 'Registers on: event unavailable';
        unavailable = true;
    }
    if (!text) return null;
    return (
        <span
            data-testid="form-event-chip"
            title={text}
            className={`inline-flex max-w-full items-center rounded-full border px-2 py-0.5 font-medium ${
                unavailable ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-stone-200 bg-stone-50 text-stone-700'
            }`}
        >
            <span className="truncate">{text}</span>
        </span>
    );
}

export default function Forms() {
    const { getAuthHeader, user } = useAuth();
    const navigate = useNavigate();
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [createName, setCreateName] = useState('');
    const [creating, setCreating] = useState(false);
    const [embedForm, setEmbedForm] = useState(null);

    const loadForms = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/forms`, { params: { page: 1, page_size: 100 }, headers: getAuthHeader() });
            setForms(res.data?.data || res.data || []);
        } catch { toast.error('Failed to load forms'); }
        finally { setLoading(false); }
    }, [getAuthHeader]);

    useEffect(() => { loadForms(); }, [loadForms]);

    const handleCreate = async () => {
        if (!createName.trim()) { toast.error('Form name required'); return; }
        setCreating(true);
        try {
            const res = await axios.post(`${API}/forms`, { name: createName }, { headers: getAuthHeader() });
            toast.success('Form created');
            setShowCreate(false);
            setCreateName('');
            navigate(`/forms/${res.data.form_id}/workflow`);
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
        finally { setCreating(false); }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/forms/${id}`, { headers: getAuthHeader() });
            toast.success('Form deleted');
            loadForms();
        } catch { toast.error('Failed to delete'); }
    };

    const handleToggle = async (form) => {
        try {
            await axios.put(`${API}/forms/${form.form_id}`, { is_active: !form.is_active }, { headers: getAuthHeader() });
            toast.success(form.is_active ? 'Form deactivated' : 'Form activated');
            loadForms();
        } catch { toast.error('Failed to update'); }
    };

    const copyUrl = (slug) => {
        navigator.clipboard.writeText(publicFormUrl(slug));
        toast.success('Public URL copied');
    };

    const filtered = useMemo(() => forms.filter(f => f.name?.toLowerCase().includes(search.toLowerCase())), [forms, search]);

    if (loading) {
        return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 text-stone-400 animate-spin" /></div>;
    }

    return (
        <div className="min-h-screen bg-stone-50 p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Forms</h1>
                    <p className="text-sm text-stone-500 mt-1">Create embeddable forms to capture leads and trigger workflows</p>
                </div>
                {(user?.is_admin) && (
                    <Button onClick={() => setShowCreate(true)}>
                        <Plus className="w-4 h-4 mr-1.5" /> New Form
                    </Button>
                )}
            </div>

            {/* KPI */}
            <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <ClipboardEdit className="w-4 h-4 text-stone-400" />
                        <span className="text-xs text-stone-500">Total Forms</span>
                    </div>
                    <span className="text-2xl font-bold text-stone-900">{forms.length}</span>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <ToggleRight className="w-4 h-4 text-stone-400" />
                        <span className="text-xs text-stone-500">Active</span>
                    </div>
                    <span className="text-2xl font-bold text-stone-900">{forms.filter(f => f.is_active).length}</span>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <Users className="w-4 h-4 text-stone-400" />
                        <span className="text-xs text-stone-500">Total Submissions</span>
                    </div>
                    <span className="text-2xl font-bold text-stone-900">{forms.reduce((s, f) => s + (f.submission_count || 0), 0)}</span>
                </div>
            </div>

            {/* Search */}
            <div className="relative max-w-sm mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search forms..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
            </div>

            {/* Form grid */}
            {filtered.length === 0 ? (
                <div className="text-center py-16">
                    <ClipboardEdit className="w-12 h-12 text-stone-200 mx-auto mb-3" />
                    <p className="text-stone-400">No forms yet. Create your first one!</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(f => (
                        <div key={f.form_id} className="bg-white rounded-xl border border-stone-200 p-5 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-semibold text-stone-900 truncate">{f.name}</h3>
                                    <span className="text-xs text-stone-400 font-mono">/{f.slug}</span>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    f.status === 'published' ? 'bg-emerald-100 text-emerald-700' :
                                    f.status === 'archived' ? 'bg-stone-100 text-stone-400' :
                                    'bg-amber-50 text-amber-700'
                                }`}>
                                    {f.status === 'published' ? 'Published' : f.status === 'archived' ? 'Archived' : 'Draft'}
                                </span>
                            </div>
                            {f.description && <p className="text-xs text-stone-500 mb-2 line-clamp-2">{f.description}</p>}
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-500 mb-3">
                                <span>{f.fields?.length || f.steps?.reduce((n, s) => n + (s.fields?.length || 0), 0) || 0} fields</span>
                                <span>{f.steps?.length || 1} step{(f.steps?.length || 1) > 1 ? 's' : ''}</span>
                                <span>{f.submission_count || 0} submissions</span>
                                <EventChip form={f} />
                            </div>
                            {f.forwarders && f.forwarders.length > 0 && (
                                <div className="flex gap-1 mb-3 flex-wrap">
                                    {f.forwarders.map((fwd, i) => (
                                        <span key={i} className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                            fwd.type === 'gohighlevel' ? 'bg-emerald-50 text-emerald-700' : 'bg-violet-50 text-violet-700'
                                        }`}>{fwd.type === 'gohighlevel' ? 'GHL' : 'API'}</span>
                                    ))}
                                </div>
                            )}
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <Button variant="outline" size="sm" onClick={() => navigate(`/forms/${f.form_id}/workflow`)}>
                                    <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => copyUrl(f.slug)}>
                                    <Copy className="w-3.5 h-3.5 mr-1" /> URL
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => setEmbedForm(f)}>
                                    <Code className="w-3.5 h-3.5 mr-1" /> Embed
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => navigate(`/forms/${f.form_id}/data`)}>
                                    <Eye className="w-3.5 h-3.5 mr-1" /> {f.submission_count || 0}
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => handleToggle(f)}>
                                    {f.is_active ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                                </Button>
                                <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDelete(f.form_id, f.name)}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Modal */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader><DialogTitle>Create Form</DialogTitle></DialogHeader>
                    <div className="py-2">
                        <label className="block text-xs font-medium text-stone-600 mb-1">Form Name</label>
                        <input value={createName} onChange={e => setCreateName(e.target.value)}
                            placeholder="e.g. Webinar Registration" autoFocus
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            onKeyDown={e => e.key === 'Enter' && handleCreate()} />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                        <Button onClick={handleCreate} disabled={creating}>{creating ? 'Creating...' : 'Create Form'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Embed Code Modal */}
            <EmbedCodeModal open={!!embedForm} onClose={() => setEmbedForm(null)} title={embedForm?.name} slug={embedForm?.slug} type="form" />
        </div>
    );
}
